import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';

const ForgotPassword = ({ switchToLogin }) => {
  const [step, setStep] = useState('email'); // Steps: email, code, password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(600); // Timer in seconds (10 minutes)
  const [userId, setUserId] = useState(null); // Store user ID after verification

  const startTimer = () => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setError('Verification code expired.');
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgotPassword`, { email });
      setStep('code');
      setTimer(600);
      startTimer();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/verifyResetCode`, 
        {"resetCode": code.toString() },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      console.log(response.data);
      setUserId(response.data.userId);
      setStep('password');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!email || !password) {
      setError('Email and password are required');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/auth/resetPassword`,
        {
          email: email.trim(),
          newPassword: password.trim()
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        setStep('success');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onClose={() => {}}>
      <div className="fixed inset-0 bg-ink/40 z-40" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
        <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white border border-sand p-6 shadow-card">
          <Dialog.Title as="h3" className="text-center text-2xl font-display font-normal tracking-tight mb-6">
            {step === 'email' && 'Forgot Password'}
            {step === 'code' && 'Enter Verification Code'}
            {step === 'password' && 'Reset Password'}
          </Dialog.Title>

          {step === 'email' && (
            <form className="flex flex-col gap-4" onSubmit={handleSendCode}>
              <input
                type="email"
                className="rounded-xl border border-sand p-3 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition"
                placeholder="Email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                type="submit"
                className="bg-ink text-cream rounded-xl py-2 px-4 hover:bg-brand-500 hover:text-ink transition-colors"
                disabled={loading}
              >
                {loading ? <div className="flex justify-center"><LoadingSpinner /></div> : 'Send Verification Code'}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form className="flex flex-col gap-4" onSubmit={handleVerifyCode}>
              <input
                type="text"
                className="rounded-xl border border-sand p-3 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition"
                placeholder="Enter Verification Code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <p className="text-clay text-sm text-center">
                Code expires in {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
              </p>
              <button
                type="submit"
                className="bg-ink text-cream rounded-xl py-2 px-4 hover:bg-brand-500 hover:text-ink transition-colors"
                disabled={loading}
              >
                {loading ? <div className="flex justify-center"><LoadingSpinner /></div> : 'Verify Code'}
              </button>
            </form>
          )}

          {step === 'password' && (
            <form className="flex flex-col gap-4" onSubmit={handleResetPassword}>
              <input
                type="password"
                className="rounded-xl border border-sand p-3 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition"
                placeholder="New Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <input
                type="password"
                className="rounded-xl border border-sand p-3 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition"
                placeholder="Confirm New Password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="submit"
                className="bg-ink text-cream rounded-xl py-2 px-4 hover:bg-brand-500 hover:text-ink transition-colors"
                disabled={loading}
              >
                {loading ? <div className="flex justify-center"><LoadingSpinner /></div> : 'Reset Password'}
              </button>
            </form>
          )}

          {error && <p className="text-red-500 text-center mt-2">{error}</p>}
          <p className="text-center text-clay text-sm mt-4">
            Remember your password?{' '}
            <span
              onClick={switchToLogin}
              className="text-brand-600 hover:text-brand-700 underline font-semibold cursor-pointer"
            >
              Log in
            </span>
          </p>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ForgotPassword;
